import { App, Button, Flex, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { FloatingWindow } from '@/shared/ui/FloatingWindow';
import { formatDate } from '@/shared/lib/date';
import { translateApiError } from '@/shared/api/errorToMessage';
import {
  useAcceptOrderRequestMutation,
  useCreateSaleFromOrderRequestMutation,
  useGetOrderRequestQuery,
  useRejectOrderRequestMutation,
} from '@/entities/order-request';
import type { OrderRequestLine, OrderRequestStatus } from '@/entities/order-request';
import { useWindowManager } from '@/shared/lib/windows';

const STATUS_LABEL: Record<OrderRequestStatus, string> = { NEW: 'Новая', ACCEPTED: 'Принята', REJECTED: 'Отклонена', DONE: 'Создана продажа' };
const STATUS_COLOR: Record<OrderRequestStatus, string> = { NEW: 'blue', ACCEPTED: 'green', REJECTED: 'red', DONE: 'default' };

export interface OrderRequestWindowProps {
  id: string;
  onClose: () => void;
}

/** §10: заявка на товар — просмотр, принять/отклонить, создать продажу из принятой заявки. */
export function OrderRequestWindow({ id, onClose }: OrderRequestWindowProps) {
  const { message } = App.useApp();
  const windows = useWindowManager();
  const { data: request, isFetching } = useGetOrderRequestQuery(id);
  const [accept, { isLoading: isAccepting }] = useAcceptOrderRequestMutation();
  const [reject, { isLoading: isRejecting }] = useRejectOrderRequestMutation();
  const [createSale, { isLoading: isCreatingSale }] = useCreateSaleFromOrderRequestMutation();

  const columns: ColumnsType<OrderRequestLine> = [
    {
      title: 'Товар',
      dataIndex: ['category', 'name'],
      render: (name: string, line) =>
        line.category.isArchived ? (
          <span>
            {name} <Tag color="warning">архив</Tag>
          </span>
        ) : (
          name
        ),
    },
    { title: 'Коробок', dataIndex: 'boxes', width: 90, align: 'right' },
    { title: 'Комментарий', dataIndex: 'comment', render: (v: string | null) => v ?? '' },
  ];

  async function handleAccept() {
    try {
      await accept(id).unwrap();
      message.success('Заявка принята');
    } catch (e) {
      message.error(translateApiError(e));
    }
  }

  async function handleReject() {
    try {
      await reject(id).unwrap();
      message.success('Заявка отклонена');
    } catch (e) {
      message.error(translateApiError(e));
    }
  }

  async function handleCreateSale() {
    try {
      const res = await createSale(id).unwrap();
      if (res.warnings.length > 0) {
        message.warning('Черновик продажи создан с предупреждениями: в заявке есть архивные товары — замените их перед проведением');
      } else {
        message.success('Создан черновик продажи');
      }
      windows.open({ kind: 'document', type: 'SALE', id: res.id });
    } catch (e) {
      message.error(translateApiError(e));
    }
  }

  return (
    <FloatingWindow kind="order-request" title={request ? `Заявка от ${request.counterparty?.name ?? '—'}` : 'Заявка на товар'} onClose={onClose} footer={renderFooter()}>
      {isFetching && !request ? (
        <Typography.Text type="secondary">Загрузка…</Typography.Text>
      ) : request ? (
        <Flex vertical gap={8}>
          <Flex gap={16} align="center">
            <Typography.Text style={{ fontSize: 12 }}>Дата: {formatDate(request.createdAt)}</Typography.Text>
            <Tag color={STATUS_COLOR[request.status]}>{STATUS_LABEL[request.status]}</Tag>
          </Flex>
          {request.comment ? <Typography.Text style={{ fontSize: 12 }}>Комментарий: {request.comment}</Typography.Text> : null}
          <Table<OrderRequestLine> size="small" rowKey="id" dataSource={request.lines} columns={columns} pagination={false} />
        </Flex>
      ) : null}
    </FloatingWindow>
  );

  function renderFooter() {
    if (!request) return null;
    return (
      <Space wrap>
        {request.status === 'NEW' ? (
          <>
            <Button type="primary" loading={isAccepting} onClick={() => void handleAccept()}>
              Принять
            </Button>
            <Button danger loading={isRejecting} onClick={() => void handleReject()}>
              Отклонить
            </Button>
          </>
        ) : null}
        {request.status === 'ACCEPTED' ? (
          <Button type="primary" loading={isCreatingSale} onClick={() => void handleCreateSale()}>
            Создать продажу
          </Button>
        ) : null}
        {request.status === 'DONE' && request.convertedDocumentId ? (
          <Button onClick={() => windows.open({ kind: 'document', type: 'SALE', id: request.convertedDocumentId! })}>Открыть продажу</Button>
        ) : null}
        <Button onClick={onClose}>Закрыть</Button>
      </Space>
    );
  }
}
